import { faArrowRight, faCheck, faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useWorkspaces } from "../state";
import { Cube } from "../state/types";
import { isExploration } from "../workspaces/kind";
import { parcelPayload } from "../workspaces/parcelPayload";
import { useWorkspaceKavel } from "./kavel";

const area = (cube: Cube) => {
  const m2 = (cube.geometry as any)?.properties?.area;
  return typeof m2 === "number" ? `${Math.round(m2).toLocaleString("nl-NL")} m²` : null;
};

const addressLine = (cube: Cube) => {
  const first = cube.addresses?.[0];
  if (!first) return null;
  return [[first.street, first.number].filter(Boolean).join(" "), first.city]
    .filter(Boolean)
    .join(", ");
};

const AddButton = ({ cube, workspace }: { cube: Cube; workspace: any }) => {
  const addParcelToWorkspace = useWorkspaces((s) => s.addParcelToWorkspace);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Zelfde check als in report/CTABox.tsx; de backend weigert een dubbele naam
  // sowieso met een 409 (unique_parcel_per_project).
  const exploration = isExploration(workspace);
  const existing = workspace.parcels?.find((p: any) => p.name === cube.name);
  if (existing) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-green">
        <FontAwesomeIcon icon={faCheck} />
        {exploration ? "Op de shortlist" : "In dit project"}
      </span>
    );
  }

  const onAdd = async () => {
    setBusy(true);
    setError(null);
    const ok = await addParcelToWorkspace(workspace.id, parcelPayload(cube));
    setBusy(false);
    if (!ok) setError("Toevoegen mislukt — mogelijk staat dit kavel er al in");
  };

  return (
    <div>
      <button
        onClick={onAdd}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-md bg-bright-yellow px-3 py-1.5 text-xs font-bold hover:bg-yellow disabled:opacity-50"
      >
        <FontAwesomeIcon icon={faPlus} />
        {busy ? "Bezig…" : exploration ? "Op de shortlist" : "Toevoegen aan project"}
      </button>
      {error && <p className="pt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

// Kaart met de kerngegevens van het aangeklikte kavel, over de kaart heen zodat
// je ziet waar het ligt terwijl je beslist.
export const KavelDetail = ({ workspace }: { workspace: any }) => {
  const { state, cube, error, clear, openReport } = useWorkspaceKavel();
  if (!state) return null;

  return (
    <div className="absolute bottom-3 left-3 z-10 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
      <button
        onClick={clear}
        className="float-right text-gray-400 hover:text-black"
        aria-label="Sluiten"
      >
        <FontAwesomeIcon icon={faXmark} size="sm" />
      </button>

      {state === "fetching" && <p className="text-xs text-gray-500">Kavel ophalen…</p>}
      {state === "error" && <p className="text-xs text-red-600">{error}</p>}

      {state === "fetched" && cube && (
        <>
          <p className="pr-4 text-sm font-bold">{cube.name}</p>
          {addressLine(cube) && <p className="text-xs text-gray-600">{addressLine(cube)}</p>}
          {area(cube) && <p className="text-xs text-gray-600">{area(cube)}</p>}
          <div className="flex items-center justify-between gap-2 pt-2">
            <AddButton cube={cube} workspace={workspace} />
            <button
              onClick={openReport}
              className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 hover:underline"
              title="Toon het volledige rapport naast de kaart"
            >
              Details <FontAwesomeIcon icon={faArrowRight} size="sm" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};
